import React, { useCallback, useMemo } from 'react'

import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'
import Button from '@/components/common/Button'
import { getEntityTypeLabel } from '@/components/library-v2/ui/Entity'

import { useConfirmCompanies } from '@/hooks/library/useConfirmCompanies'
import { useConfirmPeople } from '@/hooks/library/useConfirmPeople'
import { useDeleteCompanies } from '@/hooks/library/useDeleteCompanies'
import { useDeletePeople } from '@/hooks/library/useDeletePeople'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { UUID } from '@/types/Id'
import type { Entity } from '@/types/library/Entity'

interface ConfirmDialogLabelProps {
  single: string
  plural: string
}

interface ConfirmDialogProps {
  ids: UUID[]
  label: ConfirmDialogLabelProps
  open: boolean
  isConfirming: boolean
  setOpen: (open: boolean) => void
  onConfirm: (() => Promise<void>) | (() => void)
  onCancel?: () => void
  onDelete?: (() => Promise<void>) | (() => void)
  isDeleting?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  ids,
  label: { single, plural },
  open,
  isConfirming,
  setOpen,
  onConfirm,
  onCancel,
  onDelete,
  isDeleting = false,
}) => {
  const handleCancel = useCallback(() => {
    setOpen(false)
    onCancel?.()
  }, [setOpen, onCancel])

  const handleConfirm = useCallback(async () => {
    try {
      const promise = onConfirm()
      if (promise instanceof Promise) {
        await promise
      }
      setOpen(false)
    } catch (error) {
      console.error(error)
    }
  }, [onConfirm, setOpen])

  const handleDelete = useCallback(async () => {
    if (!onDelete) return
    try {
      const promise = onDelete()
      if (promise instanceof Promise) {
        await promise
      }
      setOpen(false)
    } catch (error) {
      console.error(error)
    }
  }, [onDelete, setOpen])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open) {
        onCancel?.()
      }
    },
    [setOpen, onCancel],
  )

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="w-[500px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Confirm {ids.length === 1 ? `this ${single}` : `these ${plural}`}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Confirming {ids.length === 1 ? `this ${single}` : `these ${plural}`}{' '}
            will add {ids.length === 1 ? 'it' : 'them'} to your library.
          </AlertDialogDescription>
          {onDelete && (
            <AlertDialogDescription>
              You can also <span className="font-semibold">delete</span>{' '}
              {ids.length === 1 ? `this ${single}` : `these ${plural}`} instead.
              Deleting will remove {ids.length === 1 ? 'it' : 'them'} from your
              Library.
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <AlertDialogCancel variant="outline-compact" onClick={handleCancel}>
            Cancel
          </AlertDialogCancel>
          <div className="flex gap-2">
            {onDelete && (
              <Button
                variant="danger-compact"
                onClick={handleDelete}
                disabled={ids.length === 0 || isDeleting || isConfirming}
                isLoading={isDeleting}
              >
                <span>
                  Delete {ids.length.toLocaleString()}{' '}
                  {ids.length === 1 ? single : plural}
                </span>
              </Button>
            )}
            <Button
              variant="positive-compact"
              onClick={handleConfirm}
              disabled={ids.length === 0 || isConfirming || isDeleting}
              isLoading={isConfirming}
            >
              <span>
                Confirm {ids.length.toLocaleString()}{' '}
                {ids.length === 1 ? single : plural}
              </span>
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ConfirmEntitiesDialogProps {
  entities: Entity[]
  open: boolean
  setOpen: (open: boolean) => void
  onSuccess?: () => void
}

export const ConfirmEntitiesDialog: React.FC<ConfirmEntitiesDialogProps> = ({
  entities,
  open,
  setOpen,
  onSuccess,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutateAsync: confirmPeople, isPending: isConfirmingPeople } =
    useConfirmPeople({ orgId: activeOrgId, optimisticUpdate: true })
  const { mutateAsync: confirmCompanies, isPending: isConfirmingCompanies } =
    useConfirmCompanies({ orgId: activeOrgId, optimisticUpdate: true })
  const { mutate: deletePeople, isPending: isDeletingPeople } = useDeletePeople(
    { orgId: activeOrgId },
  )
  const { mutate: deleteCompanies, isPending: isDeletingCompanies } =
    useDeleteCompanies({ orgId: activeOrgId })

  const { single, plural } = getEntityTypeLabel(entities[0])

  const ids = useMemo(() => entities.map((entity) => entity.id), [entities])

  const isConfirming = isConfirmingPeople || isConfirmingCompanies
  const isDeleting = isDeletingPeople || isDeletingCompanies

  const handleConfirm = useCallback(async () => {
    try {
      const companies = entities.filter(
        (entity) => entity.entity_type === 'company',
      )
      const people = entities.filter(
        (entity) => entity.entity_type === 'person',
      )
      await Promise.all([
        confirmPeople({ personIds: people.map((person) => person.id) }),
        confirmCompanies({
          companyIds: companies.map((company) => company.id),
        }),
      ])
      setOpen(false)
      toast.success(
        `${ids.length.toLocaleString()} ${ids.length === 1 ? single : plural} confirmed successfully!`,
      )
      onSuccess?.()
    } catch (error) {
      console.error(error)
    }
  }, [
    ids.length,
    single,
    plural,
    entities,
    confirmPeople,
    confirmCompanies,
    setOpen,
    onSuccess,
  ])

  const handleDelete = useCallback(async () => {
    try {
      const companies = entities.filter(
        (entity) => entity.entity_type === 'company',
      )
      const people = entities.filter(
        (entity) => entity.entity_type === 'person',
      )
      await Promise.all([
        people.length > 0
          ? new Promise<void>((resolve, reject) => {
              deletePeople(
                { ids: people.map((person) => person.id) },
                {
                  onSuccess: resolve,
                  onError: reject,
                },
              )
            })
          : Promise.resolve(),
        companies.length > 0
          ? new Promise<void>((resolve, reject) => {
              deleteCompanies(
                { ids: companies.map((company) => company.id) },
                {
                  onSuccess: resolve,
                  onError: reject,
                },
              )
            })
          : Promise.resolve(),
      ])
      setOpen(false)
      toast.success(
        `${ids.length.toLocaleString()} ${ids.length === 1 ? single : plural} deleted successfully.`,
      )
      onSuccess?.()
    } catch (error) {
      console.error(error)
    }
  }, [
    ids.length,
    single,
    plural,
    entities,
    deletePeople,
    deleteCompanies,
    setOpen,
    onSuccess,
  ])

  return (
    <ConfirmDialog
      ids={ids}
      label={{ single, plural }}
      open={open}
      isConfirming={isConfirming}
      isDeleting={isDeleting}
      setOpen={setOpen}
      onConfirm={handleConfirm}
      onDelete={handleDelete}
    />
  )
}
